import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { User } from '../../../hooks/admin/useUsers';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { useToast } from '../../../hooks/useToast';

interface ExtendedUser extends User {
  id?: string;
}

interface LevelUpRequest {
  uid: string;
  name: string;
  email: string;
  organization: string;
  currentLevel: number;
  targetLevel: number;
  reason: string;
  attachmentUrl?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
  updatedAt: any;
}

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser, userLevel: currentUserLevel, permissions, loading: authLoading } = useAdminAuth();
  const { showToast } = useToast();

  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [levelUpRequest, setLevelUpRequest] = useState<LevelUpRequest | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // 유틸리티 함수들 (React Hook 규칙을 준수하기 위해 컴포넌트 상단에 배치)
  const getUserLevelText = useCallback((level: number): string => {
    switch (level) {
      case 1: return '최고 관리자';
      case 2: return '일반 관리자';
      case 3: return '공사 관계자';
      case 4: return '센터 관계자';
      case 5: return '입주업체';
      case 6: return '입주협력업체';
      case 7: return '공공기관';
      case 8: return '항만근로자';
      case 9: return '일반 사용자';
      default: return '알 수 없음';
    }
  }, []);

  const getStatusText = useCallback((status: string): string => {
    switch (status) {
      case 'active': return '활성';
      case 'inactive': return '비활성';
      case 'suspended': return '일시 정지';
      case 'blocked': return '차단됨';
      default: return '알 수 없음';
    }
  }, []);

  const getStatusColor = useCallback((status: string): string => {
    switch (status) {
      case 'active': return 'text-green-800 bg-green-100 border-green-200';
      case 'inactive': return 'text-gray-800 bg-gray-100 border-gray-200';
      case 'suspended': return 'text-yellow-800 bg-yellow-100 border-yellow-200';
      case 'blocked': return 'text-red-800 bg-red-100 border-red-200';
      default: return 'text-gray-800 bg-gray-100 border-gray-200';
    }
  }, []);

  const getLevelUpStatusText = useCallback((status: string): string => {
    switch (status) {
      case 'pending': return '대기중';
      case 'approved': return '승인됨';
      case 'rejected': return '거절됨';
      default: return '알 수 없음';
    }
  }, []);

  const getLevelUpStatusColor = useCallback((status: string): string => {
    switch (status) {
      case 'pending': return 'text-yellow-800 bg-yellow-100 border-yellow-200';
      case 'approved': return 'text-green-800 bg-green-100 border-green-200';
      case 'rejected': return 'text-red-800 bg-red-100 border-red-200';
      default: return 'text-gray-800 bg-gray-100 border-gray-200';
    }
  }, []);

  // 날짜 포맷팅 함수
  const formatDate = useCallback((dateString: string): string => {
    if (!dateString || dateString === 'Invalid Date') return '정보 없음';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '정보 없음';
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Firestore Timestamp를 Date로 변환하는 헬퍼 함수
  const convertTimestamp = useCallback((timestamp: any): string => {
    if (!timestamp) return '';
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toISOString();
    }
    if (typeof timestamp === 'number') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? '' : date.toISOString();
    }
    if (typeof timestamp === 'string') {
      return timestamp;
    }
    return '';
  }, []);

  // 권한 체크 - 인증이 완료된 후에만 확인
  const hasPermission = useMemo(() => {
    if (authLoading) return null; // 인증 중에는 null 반환
    if (!currentUser || !currentUserLevel) return false;
    // 최고관리자이거나 사용자 관리 권한이 있는 경우
    return currentUserLevel === 1 || permissions?.users;
  }, [currentUser, currentUserLevel, permissions, authLoading]);

  const fetchUserData = useCallback(async () => {
    if (!userId) return;

    try {
      setDataLoading(true);
      setError(null);

      // 사용자 프로필 조회
      const userDoc = await getDoc(doc(db, 'user_profiles', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUser({
          id: userDoc.id,
          uid: userData.uid || userId,
          name: userData.name || '이름 없음',
          email: userData.email || '',
          phoneNumber: userData.phoneNumber || '',
          organization: userData.organization || '',
          role: userData.role || '',
          status: userData.status || 'active',
          userLevel: userData.userLevel || 9,
          lastLoginAt: convertTimestamp(userData.lastLoginAt),
          createdAt: convertTimestamp(userData.createdAt),
          updatedAt: convertTimestamp(userData.updatedAt)
        });
      } else {
        setError('사용자를 찾을 수 없습니다.');
        return;
      }

      // 등업 신청 내역 조회
      const levelUpQuery = query(
        collection(db, 'levelup_requests'),
        where('uid', '==', userId)
      );
      const levelUpSnapshot = await getDocs(levelUpQuery);

      if (!levelUpSnapshot.empty) {
        // 가장 최근 등업 신청을 가져옵니다
        const latestRequest = levelUpSnapshot.docs.reduce((latest, current) => {
          const latestData = latest.data();
          const currentData = current.data();

          if (!latestData.createdAt) return current;
          if (!currentData.createdAt) return latest;

          return latestData.createdAt.toDate() > currentData.createdAt.toDate() ? latest : current;
        });

        setLevelUpRequest({
          ...latestRequest.data() as LevelUpRequest,
          uid: latestRequest.id
        });
      }
    } catch (err) {
      console.error('데이터 로딩 중 오류:', err);
      setError(err instanceof Error ? err.message : '사용자 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setDataLoading(false);
    }
  }, [userId, convertTimestamp]);

  // 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 권한 확인 및 데이터 로딩 제어
  useEffect(() => {
    if (authLoading) return; // 인증 중에는 아무것도 하지 않음

    if (hasPermission === false) {
      setError('사용자 정보에 접근할 권한이 없습니다.');
      return;
    }

    if (hasPermission === true) {
      fetchUserData();
    }
  }, [hasPermission, authLoading, fetchUserData]);

  const handleApproveRequest = useCallback(async () => {
    if (!user || !levelUpRequest || !userId || !hasPermission) return;

    try {
      setProcessing(true);

      // 1. 사용자의 userLevel 업데이트
      const userDocRef = doc(db, 'user_profiles', userId);
      await updateDoc(userDocRef, {
        userLevel: levelUpRequest.targetLevel,
        updatedAt: serverTimestamp()
      });

      // 2. 등업 신청 상태 업데이트
      const requestDocRef = doc(db, 'levelup_requests', levelUpRequest.uid);
      await updateDoc(requestDocRef, {
        status: 'approved',
        updatedAt: serverTimestamp(),
        approvedBy: currentUser?.uid,
        approvedAt: serverTimestamp()
      });

      // 3. 상태 업데이트 및 데이터 새로고침
      showToast('등업 신청이 승인되었습니다.', { type: 'success' });
      await fetchUserData();
    } catch (err) {
      console.error('등업 신청 승인 중 오류:', err);
      const errorMessage = err instanceof Error ? err.message : '등업 신청을 승인하는 중 오류가 발생했습니다.';
      showToast(errorMessage, { type: 'error' });
    } finally {
      setProcessing(false);
    }
  }, [user, levelUpRequest, userId, hasPermission, currentUser, showToast, fetchUserData]);
  
  const handleRejectRequest = useCallback(async () => {
    if (!levelUpRequest || !userId || !hasPermission) return;

    try {
      setProcessing(true);

      // 등업 신청 상태 업데이트
      const requestDocRef = doc(db, 'levelup_requests', levelUpRequest.uid);
      await updateDoc(requestDocRef, {
        status: 'rejected',
        updatedAt: serverTimestamp(),
        rejectedBy: currentUser?.uid,
        rejectedAt: serverTimestamp()
      });

      // 상태 업데이트 및 데이터 새로고침
      showToast('등업 신청이 거절되었습니다.', { type: 'info' });
      await fetchUserData();
    } catch (err) {
      console.error('등업 신청 거절 중 오류:', err);
      const errorMessage = err instanceof Error ? err.message : '등업 신청을 거절하는 중 오류가 발생했습니다.';
      showToast(errorMessage, { type: 'error' });
    } finally {
      setProcessing(false);
    }
  }, [levelUpRequest, userId, hasPermission, currentUser, showToast, fetchUserData]);

  // 사용자 상태 변경 함수
  const handleStatusChange = useCallback(async (newStatus: string) => {
    if (!user || !userId || !hasPermission) return;

    try {
      setStatusUpdating(true);

      const userDocRef = doc(db, 'user_profiles', userId);
      await updateDoc(userDocRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      showToast(`사용자 상태가 '${getStatusText(newStatus)}'로 변경되었습니다.`, { type: 'success' });
      await fetchUserData();
    } catch (err) {
      console.error('상태 변경 중 오류:', err);
      const errorMessage = err instanceof Error ? err.message : '사용자 상태를 변경하는 중 오류가 발생했습니다.';
      showToast(errorMessage, { type: 'error' });
    } finally {
      setStatusUpdating(false);
    }
  }, [user, userId, hasPermission, showToast, fetchUserData, getStatusText]);

  // 통합된 로딩 상태 - 인증 중이거나 데이터 로딩 중일 때
  if (authLoading || (hasPermission === true && dataLoading)) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb] mx-auto mb-4"></div>
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              {authLoading ? '권한을 확인하는 중...' : '사용자 정보를 불러오는 중...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 또는 데이터가 없는 경우 (인증이 완료된 후에만 표시)
  if (!authLoading && (error || (hasPermission === true && !user && !dataLoading))) {
    const isPermissionError = error === '사용자 정보에 접근할 권한이 없습니다.';

    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className={`p-8 border-l-4 rounded-r-sm ${isPermissionError ? 'bg-yellow-50 border-yellow-500' : 'bg-red-50 border-red-500'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] mb-2">
                    {isPermissionError ? '접근 권한 없음' : '오류 발생'}
                  </h2>
                  <p className={`text-lg font-medium font-['Pretendard'] mb-4 ${isPermissionError ? 'text-yellow-700' : 'text-red-600'}`}>
                    {error || '사용자를 찾을 수 없습니다.'}
                  </p>
                  <div className="flex gap-3">
                    {!isPermissionError && (
                      <button
                        onClick={() => fetchUserData()}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-sm hover:bg-red-700 transition-colors"
                      >
                        다시 시도
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/admin/users')}
                      className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-sm hover:bg-gray-50 transition-colors"
                    >
                      목록으로 돌아가기
                    </button>
                  </div>
                </div>
                <svg className={`w-12 h-12 ${isPermissionError ? 'text-yellow-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isPermissionError ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 1112 0v1H3v-1z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  )}
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 권한이 없는 경우 또는 아직 데이터가 로드되지 않은 경우 아무것도 렌더링하지 않음
  if (hasPermission !== true || !user) {
    return null;
  }

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9 mb-2">
                사용자 상세 정보
              </h1>
              <p className="text-[#6b7684] text-lg font-medium">{user.name} ({user.email})</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {hasPermission && (
                <button
                  onClick={() => navigate(`/admin/users/${userId}/edit`)}
                  className="px-4 py-2 bg-[#155acb] text-white text-sm font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                >
                  수정
                </button>
              )}
              <button
                onClick={() => fetchUserData()}
                className="px-4 py-2 border border-gray-300 text-[#6b7684] text-sm font-medium font-['Pretendard'] rounded-sm hover:bg-gray-50 transition-colors"
                title="새로고침"
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                새로고침
              </button>
              <button
                onClick={() => navigate('/admin/users')}
                className="px-4 py-2 border border-gray-300 text-[#6b7684] text-sm font-medium font-['Pretendard'] rounded-sm hover:bg-gray-50 transition-colors"
              >
                목록으로 돌아가기
              </button>
            </div>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 기본 정보 카드 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8">
            <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">
              기본 정보
            </h2>
            <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">이름</label>
                    <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">{user.name}</p>
              </div>
              <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">이메일</label>
                    <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">{user.email}</p>
              </div>
              <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">소속</label>
                    <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">{user.organization}</p>
              </div>
              <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">전화번호</label>
                    <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">{user.phoneNumber}</p>
              </div>
            </div>
          </div>

          <div>
                <div className="space-y-6">
              <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">계정 상태</label>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(user.status)}`}>
                        {getStatusText(user.status)}
                      </span>
                      {hasPermission && (
                        <div className="relative group">
                          <button
                            className="text-[#155acb] hover:text-[#155acb]/80 text-sm font-medium"
                            onClick={() => document.getElementById('status-menu')?.classList.toggle('hidden')}
                          >
                            상태 변경
                          </button>
                          <div id="status-menu" className="hidden absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 min-w-[120px]">
                            {['active', 'inactive', 'suspended', 'blocked'].map((status) => (
                              <button
                                key={status}
                                onClick={() => {
                                  handleStatusChange(status);
                                  document.getElementById('status-menu')?.classList.add('hidden');
                                }}
                                disabled={statusUpdating || user.status === status}
                                className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                                  user.status === status ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                }`}
                              >
                                {getStatusText(status)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
              </div>
              <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">사용자 권한</label>
                    <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">{getUserLevelText(user.userLevel)}</p>
              </div>
              <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">가입일</label>
                    <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">{formatDate(user.createdAt)}</p>
              </div>
              <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">마지막 로그인</label>
                    <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">{formatDate(user.lastLoginAt)}</p>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
        {/* 등업 신청 정보 카드 */}
      {levelUpRequest && (
          <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
            <div className="p-8">
          <div className="flex justify-between items-center mb-4">
                <h2 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9">
                  등업 신청 정보
                </h2>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium font-['Pretendard'] border ${getLevelUpStatusColor(levelUpRequest.status)}`}>
              {getLevelUpStatusText(levelUpRequest.status)}
            </span>
          </div>
              <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-8"></div>
          
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">신청 등급</label>
                      <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">
                    {getUserLevelText(levelUpRequest.currentLevel)} → {getUserLevelText(levelUpRequest.targetLevel)}
                  </p>
                </div>
                <div>
                      <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">신청일</label>
                      <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard']">
                        {levelUpRequest.createdAt?.toDate ? formatDate(levelUpRequest.createdAt.toDate().toISOString()) : '날짜 정보 없음'}
                      </p>
                </div>
                {levelUpRequest.attachmentUrl && (
                  <div>
                        <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">첨부파일</label>
                    <div className="mt-1">
                      <a 
                        href={levelUpRequest.attachmentUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                            className="text-[#155acb] text-base font-semibold font-['Pretendard'] hover:text-[#155acb]/80 transition-colors"
                      >
                        증빙서류 보기
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <div>
                    <label className="block text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-2">신청 사유</label>
                    <p className="text-[#1e2c49] text-[19px] font-semibold font-['Pretendard'] whitespace-pre-line">{levelUpRequest.reason}</p>
              </div>
            </div>
          </div>
          
          {levelUpRequest.status === 'pending' && (
                <div className="mt-8 flex justify-end space-x-4">
              <button
                onClick={handleRejectRequest}
                disabled={processing}
                    className="w-[130px] h-11 rounded-sm border border-[#ed5a5a] text-[#ed5a5a] text-base font-bold font-['Pretendard'] hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {processing ? '처리 중...' : '등업 신청 거절'}
              </button>
              <button
                onClick={handleApproveRequest}
                disabled={processing}
                    className="w-[130px] h-11 bg-green-600 text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {processing ? '처리 중...' : '등업 신청 승인'}
              </button>
            </div>
          )}
            </div>
        </div>
      )}

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default UserDetailPage;
